import { lazyImport } from '@/lib/lazyImport'
import { RegistrationType } from '@/@types/generated'

// ----------------------------------------------------------
// Lazy load components for each route to optimize performance
// ----------------------------------------------------------
const Index = lazyImport(() => import('./components/Pages/Index'))

// ----------------------------------------------------------
// Auth routes
// ----------------------------------------------------------
const Signup = lazyImport(() => import('./components/Pages/Auth/Signup'))
const ForgotPassword = lazyImport(() => import('./components/Pages/Auth/ForgotPassword'))
const ResetPassword = lazyImport(() => import('./components/Pages/Auth/ResetPassword'))
const SignupEmailSent = lazyImport(() => import('./components/Pages/Auth/SignupEmailSent'))
const UserEmailConfirmed = lazyImport(() => import('./components/Pages/Auth/UserEmailConfirmed'))
const ConfirmNewEmail = lazyImport(() => import('./components/Pages/Profile/ConfirmNewEmail'))
const ConfirmNewSecondaryEmail = lazyImport(() => import('./components/Pages/Profile/ConfirmNewSecondaryEmail'))

// ----------------------------------------------------------
// TruckStop routes
// ----------------------------------------------------------
const TruckStopIndex = lazyImport(() => import('./modules/truckstop/routes/TruckStopIndex'))
const TruckStopSearch = lazyImport(() => import('./modules/truckstop/routes/TruckStopSearch'))
const TruckStopCompanyDetailsIndex = lazyImport(() => import('./modules/truckstop/routes/TruckStopCompanyDetailsIndex'))
const TruckStopCompanyDetailsCertificate = lazyImport(() => import('./modules/truckstop/routes/company-details/CertificateIndex.tsx'))
const TruckStopCompanyDetailsAutomatedMotorVehicles = lazyImport(() => import('./modules/truckstop/routes/company-details/AutomatedMotorVehiclesIndex.tsx'))
const TruckStopComplaintsIndex = lazyImport(() => import('./modules/truckstop/routes/TruckStopComplaintsIndex'))
const TruckStopComplaintsFaq = lazyImport(() => import('./modules/truckstop/routes/TruckStopComplaintsFaq'))
const TruckStopComplaintSubmitted = lazyImport(() => import('./modules/truckstop/routes/TruckStopComplaintSubmitted'))

// ----------------------------------------------------------
// Dashboard routes
// ----------------------------------------------------------
const DashboardIndex = lazyImport(() => import('./modules/dashboard/routes/DashboardIndex'))

// ----------------------------------------------------------
// Companies routes
// ----------------------------------------------------------
const CompaniesIndex = lazyImport(() => import('./modules/companies/routes/CompaniesIndex'))
const CompanyVinLookup = lazyImport(() => import('./modules/companies/routes/vin-lookup/CompanyVinLookup.tsx'))
const VinHistoryDetails = lazyImport(() => import('./modules/companies/routes/vin-lookup/VinHistoryDetails.tsx'))
const CompanyDetailsIndex = lazyImport(() => import('./modules/companies/routes/company-details/CompanyDetailsIndex'))
const CompanyDetailsCertificate = lazyImport(() => import('./modules/companies/routes/company-details/CompanyDetailsCertificate'))
const CompanyDetailsInsurance = lazyImport(() => import('./modules/companies/routes/company-details/CompanyDetailsInsurance'))
const CompanyDetailsHhgTariff = lazyImport(() => import('./modules/companies/routes/company-details/CompanyDetailsHhgTariff'))
const CompanyDetailsAutonomousVehicles = lazyImport(() => import('./modules/companies/routes/company-details/CompanyDetailsAutonomousVehicles'))
const CompanyDetailsHistory = lazyImport(() => import('./modules/companies/routes/company-details/CompanyHistory'))
const CompanyDetailsPayments = lazyImport(() => import('./modules/companies/routes/company-details/CompanyDetailsPayments'))
const CompanyUserDetails = lazyImport(() => import('./modules/companies/routes/users/CompanyUserDetails'))
const CompanyUsersIndex = lazyImport(() => import('./modules/companies/routes/users/CompanyUsersIndex'))

// ----------------------------------------------------------
// Credentialing routes
// ----------------------------------------------------------
const CredentialingIndex = lazyImport(() => import('./modules/credentialing/routes/CredentialingIndex'))
const CredentialingApplicationDetails = lazyImport(() => import('./modules/credentialing/routes/ApplicationDetails'))
const ChameleonCarriersIndex = lazyImport(() => import('./modules/credentialing/routes/ChameleonCarriersIndex'))
const ChameleonCarrierDetails = lazyImport(() => import('./modules/credentialing/routes/ChameleonCarrierDetails'))
const VehicleReviewIndex = lazyImport(() => import('./modules/credentialing/routes/VehicleReviewIndex'))
const VehicleReviewDetails = lazyImport(() => import('./modules/credentialing/routes/VehicleReviewDetails'))
const BouncedEmailsIndex = lazyImport(() => import('./modules/bounced-emails/routes/BouncedEmailsIndex'))

// ----------------------------------------------------------
// Certificate Data Review routes
// ----------------------------------------------------------
const CertificateDataReviews = lazyImport(() => import('./components/Pages/CertificateDataReviews'))
const CertificateDataReviewsPage = lazyImport(() => import('./components/Pages/CertificateDataReviewsPage'))

// ----------------------------------------------------------
// Companies - Certificate Renewal routes
// ----------------------------------------------------------
const CompanyCertificateRenewVehicles = lazyImport(() => import('./modules/companies/routes/certificate/RenewVehicles'))
const CompanyCertificateRenewPeriod = lazyImport(() => import('./modules/companies/routes/certificate/RenewPeriod'))
const CompanyCertificateRenewConfirm = lazyImport(() => import('./modules/companies/routes/certificate/RenewConfirm'))
const CompanyCertificateRenewSuccess = lazyImport(() => import('./modules/companies/routes/certificate/RenewSuccess'))

// -----------------------------------------------------------
// Cases routes
// ----------------------------------------------------------
const CasesIndex = lazyImport(() => import('./modules/cases/routes/CasesIndex'))
const CasesNoddQueue = lazyImport(() => import('./modules/cases/routes/CasesNoddQueue'))
const CasesFinalOrderQueue = lazyImport(() => import('./modules/cases/routes/CasesFinalOrderQueue'))
const CaseDetailsIndex = lazyImport(() => import('./modules/cases/routes/case-details/CaseDetailsIndex'))
const CaseDetailsComplaintDetails = lazyImport(() => import('./modules/cases/routes/case-details/ComplaintDetails'))
const CaseDetailsInvestigation = lazyImport(() => import('./modules/cases/routes/case-details/CaseInvestigation'))
const CaseDetailsViolations = lazyImport(() => import('./modules/cases/routes/case-details/CaseViolations'))
const CaseDetailsNotices = lazyImport(() => import('./modules/cases/routes/case-details/CaseNotices'))
const CaseDetailsHistory = lazyImport(() => import('./modules/cases/routes/case-details/CaseHistory'))

// ----------------------------------------------------------
// Tickets routes
// ----------------------------------------------------------
const TicketsIndex = lazyImport(() => import('./modules/tickets/routes/TicketsIndex'))
const TicketCreate = lazyImport(() => import('./modules/tickets/routes/TicketCreate'))
const TicketOverview = lazyImport(() => import('./modules/tickets/routes/ticket-details/TicketOverview'))

// ----------------------------------------------------------
// Payments routes
// ----------------------------------------------------------
const PaymentsIndex = lazyImport(() => import('./modules/payments/routes/PaymentsIndex'))
const PaymentDetails = lazyImport(() => import('./modules/payments/routes/PaymentDetails'))
const PaymentProcessCharges = lazyImport(() => import('./modules/payments/routes/PaymentProcessCharges'))
const PaymentProcessAmounts = lazyImport(() => import('./modules/payments/routes/PaymentProcessAmounts'))
const PaymentProcessConfirm = lazyImport(() => import('./modules/payments/routes/PaymentProcessConfirm'))
const PaymentProcessSuccess = lazyImport(() => import('./modules/payments/routes/PaymentProcessSuccess'))
const PaymentProcessError = lazyImport(() => import('./modules/payments/routes/PaymentProcessError'))

// -----------------------------------------------------------
// Licensing Agencies routes
// ----------------------------------------------------------
const LicensingAgenciesIndex = lazyImport(() => import('./modules/licensing-agencies/routes/LicensingAgenciesIndex'))
const LicensingAgencyIndex = lazyImport(() => import('./modules/licensing-agencies/routes/LicensingAgencyIndex'))
const LicensingAgencyCompanies = lazyImport(() => import('./modules/licensing-agencies/routes/LicensingAgencyCompanies'))
const LicensingAgencyUsers = lazyImport(() => import('./modules/licensing-agencies/routes/LicensingAgencyUsers'))
const LicensingAgencyUserDetails = lazyImport(() => import('./modules/licensing-agencies/routes/LicensingAgencyUserDetails'))
const LicensingAgencyHistory = lazyImport(() => import('./modules/licensing-agencies/routes/LicensingAgencyHistory'))

// ----------------------------------------------------------
// Applications routes
// ----------------------------------------------------------
const ApplicationsIndex = lazyImport(() => import('./modules/applications/routes/ApplicationsIndex'))

// Certificate application
const CertificateIndex = lazyImport(() => import('./modules/applications/modules/certificate/routes/CertificateIndex.tsx'))
const LegalDisclaimer = lazyImport(() => import('./modules/applications/routes/LegalDisclaimer'))
const ApplicationType = lazyImport(() => import('./modules/applications/routes/ApplicationType'))
const CertificateRegistration = lazyImport(() => import('./modules/applications/modules/certificate/routes/CertificateRegistration'))
const ApplicationIndex = lazyImport(() => import('./modules/applications/modules/certificate/routes/ApplicationIndex'))

// Certificate - Motor Carrier
const ReviewSafer = lazyImport(() => import('./modules/applications/modules/certificate/routes/ReviewSafer'))
const MotorCarrierBusinessStructure = lazyImport(() => import('./modules/applications/modules/certificate/routes/MotorCarrierBusinessStructure'))
const MotorCarrierTexasAgent = lazyImport(() => import('./modules/applications/modules/certificate/routes/MotorCarrierTexasAgent'))
const MotorCarrierInterstateUcr = lazyImport(() => import('./modules/applications/modules/certificate/routes/MotorCarrierInterstateUcr'))
const MotorCarrierRegistrationPeriod = lazyImport(() => import('./modules/applications/modules/certificate/routes/MotorCarrierRegistrationPeriod'))
const MotorCarrierCargoTypes = lazyImport(() => import('./modules/applications/modules/certificate/routes/MotorCarrierCargoTypes.tsx'))
const MotorCarrierVehicles = lazyImport(() => import('./modules/applications/modules/certificate/routes/MotorCarrierVehicles'))
const MotorCarrierDrugTestCertification = lazyImport(() => import('./modules/applications/modules/certificate/routes/MotorCarrierDrugTestCertification.tsx'))
const MotorCarrierQuestionnaire = lazyImport(() => import('./modules/applications/modules/certificate/routes/MotorCarrierQuestionnaire.tsx'))
const MotorCarrierReview = lazyImport(() => import('./modules/applications/modules/certificate/routes/MotorCarrierReview.tsx'))
const MotorCarrierSubmitted = lazyImport(() => import('./modules/applications/modules/certificate/routes/MotorCarrierSubmitted'))

// Certificate - Broker
const BrokerReviewSafer = ReviewSafer // Same component as Motor Carrier flow
const BrokerStep2 = lazyImport(() => import('./components/Pages/Application/Broker/BrokerStep2'))
const BrokerApplicationComplete = lazyImport(() => import('./components/Pages/Application/Broker/BrokerApplicationComplete'))

// Certificate - Insurance Provider
const InsuranceSignUp = lazyImport(() => import('./modules/insurance/routes/InsuranceSignUp'))
const InsuranceSignupComplete = lazyImport(() => import('./modules/insurance/routes/InsuranceSignupComplete'))

// Automated Motor Vehicles application
const AutonomousVehiclesIndex = lazyImport(() => import('@/modules/applications/modules/automated-vehicles/routes/AvaIndex'))
const AutonomousVehiclesBusinessInfo = lazyImport(() => import('@/modules/applications/modules/automated-vehicles/routes/AvaBusinessInfo'))
const AutonomousVehiclesContactInfo = lazyImport(() => import('@/modules/applications/modules/automated-vehicles/routes/AvaContactInfo'))
const AutonomousVehiclesVehicles = lazyImport(() => import('@/modules/applications/modules/automated-vehicles/routes/AvaVehicles'))
const AutonomousVehiclesCertify = lazyImport(() => import('@/modules/applications/modules/automated-vehicles/routes/AvaCertifyPage'))
const AutonomousVehiclesSuccess = lazyImport(() => import('@/modules/applications/modules/automated-vehicles/routes/AvaSuccess'))

// Licensing Agency application
const LicensingAgencyApplication = lazyImport(() => import('./modules/applications/modules/licensing-agency/routes/LicensingAgency'))
const LicensingAgencyApplicationIndex = lazyImport(() => import('./modules/applications/modules/licensing-agency/routes/LicensingAgencyIndex'))
const LicensingAgencyApplicationBusinessInfo = lazyImport(() => import('./modules/applications/modules/licensing-agency/routes/LicensingAgencyBusinessInfo'))
const LicensingAgencyApplicationDocumentation = lazyImport(() => import('./modules/applications/modules/licensing-agency/routes/LicensingAgencyDocumentation'))
const LicensingAgencyApplicationCertify = lazyImport(() => import('./modules/applications/modules/licensing-agency/routes/LicensingAgencyCertify'))
const LicensingAgencyApplicationSuccess = lazyImport(() => import('./modules/applications/modules/licensing-agency/routes/LicensingAgencySuccess'))

// Insurance Users
const InsuranceUsersIndex = lazyImport(() => import('@/modules/insurance/users/routes/InsuranceUsersIndex'))

// Insurance Insurers
const InsurersIndex = lazyImport(() => import('@/modules/insurance/insurers/routes/InsurersIndex'))
const InsurerIndex = lazyImport(() => import('@/modules/insurance/insurers/routes/InsurerIndex'))
const InsurerUsers = lazyImport(() => import('@/modules/insurance/insurers/routes/InsurerUsers'))
const InsurerCompanies = lazyImport(() => import('@/modules/insurance/insurers/routes/InsurerCompanies'))

// ----------------------------------------------------------
// Users routes
// ----------------------------------------------------------
const UsersIndex = lazyImport(() => import('@/modules/users/routes/UsersIndex'))
const UserDetails = lazyImport(() => import('@/modules/users/routes/UserDetails'))
const ProfileLayout = lazyImport(() => import('./components/Pages/Profile/ProfileLayout'))

// ----------------------------------------------------------
// Help Document routes
// ----------------------------------------------------------
const HelpDocumentLayout = lazyImport(() => import('@/components/Pages/HelpDocuments/HelpDocumentLayout'))

// ----------------------------------------------------------
// Define the routes and their corresponding components
//
// Keys:   React Router paths
// Values: React components to render
//
// Paths with parameters (e.g., :code) indicate dynamic
// segments that will be replaced with actual values
// -----------------------------------------------------------
export const routes = {
  '/': <Index />,

  // Auth routes
  '/signup': <Signup />,
  '/forgot-password': <ForgotPassword />,
  '/reset-password': <ResetPassword />,
  '/email-sent': <SignupEmailSent />,
  '/confirm-email': <UserEmailConfirmed />,
  '/confirm-new-email': <ConfirmNewEmail />,
  '/confirm-new-secondary-email': <ConfirmNewSecondaryEmail />,

  // TruckStop routes
  '/truckstop': <TruckStopIndex />,
  '/truckstop/search': <TruckStopSearch />,
  '/truckstop/companies/:businessEntityId': <TruckStopCompanyDetailsIndex />,
  '/truckstop/companies/:businessEntityId/certificate': (
    <TruckStopCompanyDetailsCertificate />
  ),
  '/truckstop/companies/:businessEntityId/automated-motor-vehicles': (
    <TruckStopCompanyDetailsAutomatedMotorVehicles />
  ),

  '/truckstop/complaints': <TruckStopComplaintsIndex />,
  '/truckstop/complaints/file': <TruckStopComplaintsIndex action="file" />,
  '/truckstop/complaints/submitted': <TruckStopComplaintSubmitted />,
  '/truckstop/complaints/faq': <TruckStopComplaintsFaq />,

  // Dashboard routes
  '/dashboard': <DashboardIndex />,

  // Companies routes
  '/companies': <CompaniesIndex />,
  '/companies/vin-lookup': <CompanyVinLookup />,
  '/companies/vin-lookup/:id': <VinHistoryDetails />,
  '/companies/:businessEntityId': <CompanyDetailsIndex />,
  '/companies/:businessEntityId/certificate': <CompanyDetailsCertificate />,
  '/companies/:businessEntityId/insurance': <CompanyDetailsInsurance />,
  '/companies/:businessEntityId/hhg-tariff': <CompanyDetailsHhgTariff />,
  '/companies/:businessEntityId/automated-vehicles': <CompanyDetailsAutonomousVehicles />,
  '/companies/:businessEntityId/history': <CompanyDetailsHistory />,
  '/companies/:businessEntityId/payments': <CompanyDetailsPayments />,
  '/companies/:businessEntityId/users': <CompanyUsersIndex />,
  '/companies/:businessEntityId/users/:userId': <CompanyUserDetails />,

  // Credentialing routes
  '/credentialing': <CredentialingIndex />,
  '/credentialing/applications/:applicationId': <CredentialingApplicationDetails />,
  '/credentialing/chameleon-carriers': <ChameleonCarriersIndex />,
  '/credentialing/chameleon-carriers/:chameleonQueueId': <ChameleonCarrierDetails />,
  '/credentialing/vehicle-review': <VehicleReviewIndex />,
  '/credentialing/vehicle-review/:vehicleReviewRequestId': <VehicleReviewDetails />,
  '/bounced-emails': <BouncedEmailsIndex />,

  // Certificate Data Review routes
  '/edit-queue': <CertificateDataReviews />,
  '/edit-queue/:certificateDataReviewId': <CertificateDataReviewsPage />,

  // Companies - Certificate Renewal routes
  '/companies/:businessEntityId/certificate/renewal': <CompanyCertificateRenewVehicles />,
  '/companies/:businessEntityId/certificate/renewal/registration-period': <CompanyCertificateRenewPeriod />,
  '/companies/:businessEntityId/certificate/renewal/confirm': <CompanyCertificateRenewConfirm />,
  '/companies/:businessEntityId/certificate/renewal/success': <CompanyCertificateRenewSuccess />,
  '/companies/:businessEntityId/certificate/re-register': <CompanyCertificateRenewVehicles />,
  '/companies/:businessEntityId/certificate/re-register/registration-period': <CompanyCertificateRenewPeriod />,
  '/companies/:businessEntityId/certificate/re-register/confirm': <CompanyCertificateRenewConfirm />,
  '/companies/:businessEntityId/certificate/re-register/success': <CompanyCertificateRenewSuccess />,

  // Cases routes
  '/cases': <CasesIndex />,
  '/cases/nodd-queue': <CasesNoddQueue />,
  '/cases/final-order-queue': <CasesFinalOrderQueue />,
  '/cases/:caseId': <CaseDetailsIndex />,
  '/cases/:caseId/complaint': <CaseDetailsComplaintDetails />,
  '/cases/:caseId/investigation': <CaseDetailsInvestigation />,
  '/cases/:caseId/violations': <CaseDetailsViolations />,
  '/cases/:caseId/notices': <CaseDetailsNotices />,
  '/cases/:caseId/history': <CaseDetailsHistory />,

  // Licensing Agencies routes
  '/licensing-agencies': <LicensingAgenciesIndex />,
  '/licensing-agencies/:agencyId': <LicensingAgencyIndex />,
  '/licensing-agencies/:agencyId/companies': <LicensingAgencyCompanies />,
  '/licensing-agencies/:agencyId/users': <LicensingAgencyUsers />,
  '/licensing-agencies/:agencyId/users/:userId': <LicensingAgencyUserDetails />,
  '/licensing-agencies/:agencyId/history': <LicensingAgencyHistory />,

  // Tickets routes
  '/tickets': <TicketsIndex />,
  '/tickets/new': <TicketCreate />,
  '/tickets/:ticketId': <TicketOverview />,

  // Applications routes
  '/applications': <ApplicationsIndex />,

  // Applications - TxDMV Certificate routes
  '/applications/certificate': <CertificateIndex />,

  '/applications/certificate/legal-disclaimer': <LegalDisclaimer />,
  '/applications/certificate/type': <ApplicationType />,

  '/applications/certificate/motor-carrier': <CertificateRegistration registrationType={RegistrationType.MOTOR_CARRIER} />,
  '/applications/certificate/:applicationId/motor-carrier': <ApplicationIndex registrationType={RegistrationType.MOTOR_CARRIER} />,
  '/applications/certificate/:applicationId/motor-carrier/review-safer': <ReviewSafer />,
  '/applications/certificate/:applicationId/motor-carrier/business-information': <MotorCarrierBusinessStructure />,
  '/applications/certificate/:applicationId/motor-carrier/texas-agent': <MotorCarrierTexasAgent />,
  '/applications/certificate/:applicationId/motor-carrier/interstate-ucr': <MotorCarrierInterstateUcr />,
  '/applications/certificate/:applicationId/motor-carrier/registration-period': <MotorCarrierRegistrationPeriod />,
  '/applications/certificate/:applicationId/motor-carrier/cargo-types': <MotorCarrierCargoTypes />,
  '/applications/certificate/:applicationId/motor-carrier/vehicles': <MotorCarrierVehicles />,
  '/applications/certificate/:applicationId/motor-carrier/drug-test-certification': <MotorCarrierDrugTestCertification />,
  '/applications/certificate/:applicationId/motor-carrier/questionnaire': <MotorCarrierQuestionnaire />,
  '/applications/certificate/:applicationId/motor-carrier/review': <MotorCarrierReview />,
  '/applications/certificate/:applicationId/motor-carrier/submitted': <MotorCarrierSubmitted />,

  // TODO: Move these to /applications/broker routes
  '/applications/certificate/broker': <CertificateRegistration registrationType={RegistrationType.BROKER} />,
  '/applications/certificate/:applicationId/broker': <ApplicationIndex registrationType={RegistrationType.BROKER} />,
  '/applications/certificate/:applicationId/broker/review-safer': <BrokerReviewSafer />,
  '/applications/certificate/:applicationId/broker/documents': <BrokerStep2 />,
  '/applications/certificate/:applicationId/broker/complete': <BrokerApplicationComplete />,

  // Applications - Insurance Provider
  '/applications/insurance-provider': <InsuranceSignUp />,
  '/applications/insurance-provider/complete': <InsuranceSignupComplete />,

  // Applications - Automated Motor Vehicles routes
  '/applications/automated-vehicles': <AutonomousVehiclesIndex />,
  '/applications/automated-vehicles/:registrationId/business-info': <AutonomousVehiclesBusinessInfo />,
  '/applications/automated-vehicles/:registrationId/contact-info': <AutonomousVehiclesContactInfo />,
  '/applications/automated-vehicles/:registrationId/vehicles': <AutonomousVehiclesVehicles />,
  '/applications/automated-vehicles/:registrationId/certify': <AutonomousVehiclesCertify />,
  '/applications/automated-vehicles/:registrationId/success': <AutonomousVehiclesSuccess />,

  // Applications - Licensing Agency
  '/applications/licensing-agency': <LicensingAgencyApplicationIndex />,
  '/applications/licensing-agency/:agencyId': <LicensingAgencyApplication />,
  '/applications/licensing-agency/:agencyId/business-info': <LicensingAgencyApplicationBusinessInfo />,
  '/applications/licensing-agency/:agencyId/documentation': <LicensingAgencyApplicationDocumentation />,
  '/applications/licensing-agency/:agencyId/certify': <LicensingAgencyApplicationCertify />,
  '/applications/licensing-agency/:agencyId/success': <LicensingAgencyApplicationSuccess />,

  // Insurance routes
  '/insurance/users': <InsuranceUsersIndex />,
  '/insurance/insurers': <InsurersIndex />,
  '/insurance/insurers/:licenseNumber': <InsurerIndex />,
  '/insurance/insurers/:licenseNumber/users': <InsurerUsers />,
  '/insurance/insurers/:licenseNumber/companies': <InsurerCompanies />,

  // Payments routes
  '/payments': <PaymentsIndex />,
  '/payments/:paymentId': <PaymentDetails />,
  '/payments/process': <PaymentProcessCharges />,
  '/payments/process/amounts': <PaymentProcessAmounts />,
  '/payments/process/confirm': <PaymentProcessConfirm />,
  '/payments/process/success': <PaymentProcessSuccess />,
  '/payments/process/error': <PaymentProcessError />,

  // Profile routes
  '/profile': <ProfileLayout />,
  '/profile/:activeTab': <ProfileLayout />,

  // User routes
  '/users': <UsersIndex />,
  '/users/:userId': <UserDetails />,

  // help documents
  '/help-documents': <HelpDocumentLayout />
} as const

export type RouteKey = keyof typeof routes

// ----------------------------------------------------------
// Utility types and functions for working with routes
// ----------------------------------------------------------

type ExtractParamNames<S extends string> =
  S extends `${string}:${infer P}/${infer R}`
    ? P | ExtractParamNames<`/${R}`>
    : S extends `${string}:${infer P}`
      ? P
      : never

export type Params<S extends RouteKey> =
  ExtractParamNames<S> extends never
    ? never
    : { [K in ExtractParamNames<S>]: string }

export type RouteKeyWithParam<ParamName extends string> = {
  [K in RouteKey]: ParamName extends ExtractParamNames<K> ? K : never
}[RouteKey]


// Build a path by replacing parameter placeholders with actual values
// This is type-safe and will require the correct parameters for paths that have them
export function buildPath<P extends RouteKey>(
  path: P,
  ...args: Params<P> extends never ? [] : [params: Params<P>]
): string {
  const params = (args[0] ?? {}) as Record<string, string>
  let newPath = path as string
  for (const [key, value] of Object.entries(params)) {
    newPath = newPath.replace(`:${key}`, String(value))
  }
  return newPath
}
